import React, { useEffect, useRef, useState } from 'react';
import BaaStatusBanner from '../auth/BaaStatusBanner';
import SubscriptionBanner, { subscriptionBannerKind } from './SubscriptionBanner';
import { useAuth } from '../../context/authcontext';
import { useCanPerform } from '../../hooks/useCanPerform';
import { SubscriptionService } from '../../services/billing/subscription.service';
import { describeApiError } from '../../utils/apiErrors';
import { showsBaaBanner } from '../../utils/baaAccess';
import { ACCOUNT_STATE_CHANGED_EVENT, openOrganizationAccount } from '../../constants/navigationEvents';
import type { AuthUser } from '../../types/permissions';
import SandboxBanner from '../demo/SandboxBanner';

/** True when the account banner stack has something to show for this user. */
export function hasAccountBanners(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  // Demo sandbox: its own banner (sample-data notice, or the ended notice) replaces the subscription ones
  if (user.sandbox) return true;
  return showsBaaBanner(user) || subscriptionBannerKind(user.subscription) !== null;
}

/** Minimum gap between permission re-reads triggered by account-state 402s. */
const ACCOUNT_REFRESH_MIN_MS = 30_000;

/**
 * Re-read /users/me/permissions when an API call reports the account went read-only or
 * suspended mid-session (apiClient fires ACCOUNT_STATE_CHANGED_EVENT). Throttled.
 */
export function useAccountStateRefresh(): void {
  const { refreshPermissions } = useAuth();
  const lastRefresh = useRef(0);
  useEffect(() => {
    const onChange = (event: Event) => {
      // A closed account always refreshes so the guard can show the closed page at once
      const code = (event as CustomEvent<{ code?: string } | undefined>).detail?.code;
      if (code === 'ACCOUNT_CLOSED') lastRefresh.current = 0;
      const now = Date.now();
      if (now - lastRefresh.current < ACCOUNT_REFRESH_MIN_MS) return;
      lastRefresh.current = now;
      void refreshPermissions?.();
    };
    window.addEventListener(ACCOUNT_STATE_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(ACCOUNT_STATE_CHANGED_EVENT, onChange);
  }, [refreshPermissions]);
}

interface AccountBannersProps {
  /** Opens the BAA signing page (owners and admins). */
  onReviewBaa: () => void;
}

/**
 * Account-level notices above the main layout (Phase 13, §4.1): the BAA banner first, then the
 * subscription banner (trial, past due, read-only, cancel scheduled).
 */
const AccountBanners: React.FC<AccountBannersProps> = ({ onReviewBaa }) => {
  const { user, refreshPermissions } = useAuth();
  const canManage = useCanPerform('manage:subscription');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user || !hasAccountBanners(user)) return null;

  const openCardPage = async () => {
    setBusy(true);
    setError(null);
    try {
      SubscriptionService.redirectTo(await SubscriptionService.addPaymentMethod());
    } catch (err) {
      setError(describeApiError(err, 'Unable to open the card page.'));
      setBusy(false);
    }
  };

  const undoCancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await SubscriptionService.undoCancel();
      await refreshPermissions?.();
    } catch (err) {
      setError(describeApiError(err, 'Unable to undo the cancellation.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="account-banners">
      {showsBaaBanner(user) && <BaaStatusBanner user={user} onReview={onReviewBaa} />}
      {user.sandbox ? <SandboxBanner /> : (
        <SubscriptionBanner
          subscription={user.subscription ?? null}
          canManage={canManage}
          timeZone={user.agencyTimezone}
          onOpenAccount={openOrganizationAccount}
          onPaymentMethod={openCardPage}
          onUndoCancel={undoCancel}
          busy={busy}
          error={error}
        />
      )}
    </div>
  );
};

export default AccountBanners;
